import type { MouseEvent } from 'react';
import { EyeIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toBaseFormId } from 'components/IAP/formFamily';
import {
  useIcsFormAvailability,
  useIcsFormFamily,
} from 'components/IAP/hooks/useIcsFormFamily';
import { isPremiumResourceForm } from 'components/IAP/premiumResourceManagement';
import { usePremiumResourceManagementFlag } from 'utils/featureFlags';

/** Coast Guard editions a FEMA incident does not preview or export. */
const FEMA_EXPORT_HIDDEN = new Set(['ICS_230', 'ICS_233']);

/** Workbooks that leave the workspace as Excel, and only as Excel. */
const EXCEL_ONLY_FORMS = new Set(['CG_5136', 'CG_5136X']);

export type FormExportFormat = 'pdf' | 'docx' | 'xlsx';

/** Formats every form except the two cost workbooks can preview. */
type DocumentExportFormat = 'pdf' | 'docx';

interface FormExportButtonProps {
  formname: string;
  /** PDF and Word. The cost workbooks use `onExportExcel` instead. */
  onExport: (format: DocumentExportFormat) => void;
  /** CG-5136 and CG-5136X. Absent on every other form. */
  onExportExcel?: () => void;
  label?: string;
  disabled?: boolean;
}

const stopPropagation = (event: MouseEvent) => {
  event.stopPropagation();
};

/**
 * Preview / export control. A FEMA incident exports Word only, and does not
 * offer ICS 230 or 233 at all. Coast Guard incidents get a PDF/Word dropdown
 * when a Word template exists, otherwise a single PDF button.
 */
export function FormExportButton({
  formname,
  onExport,
  onExportExcel,
  label = 'Preview',
  disabled = false,
}: FormExportButtonProps) {
  const premium = usePremiumResourceManagementFlag();
  const family = useIcsFormFamily();
  const { available, reason, supportsDocx } = useIcsFormAvailability(formname);
  const baseId = toBaseFormId(formname);
  if (!premium && isPremiumResourceForm(formname)) return null;
  if (family === 'fema' && FEMA_EXPORT_HIDDEN.has(baseId)) {
    return null;
  }
  const isDisabled = disabled || !available;
  if (EXCEL_ONLY_FORMS.has(baseId)) {
    if (family === 'fema') return null;
    const excelButton = (
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={isDisabled}
        onClick={(event) => {
          event.stopPropagation();
          onExportExcel?.();
        }}
      >
        <EyeIcon /> {label}
      </Button>
    );
    if (!available) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              render={
                <span className="tw:inline-flex tw:cursor-not-allowed">
                  {excelButton}
                </span>
              }
            />
            <TooltipContent side="top" className="dark tw:max-w-xs">
              {reason}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return excelButton;
  }
  const wordOnly = family === 'fema';

  const button = wordOnly ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={isDisabled || !supportsDocx}
      onClick={(event) => {
        event.stopPropagation();
        onExport('docx');
      }}
    >
      <EyeIcon /> {label}
    </Button>
  ) : supportsDocx ? (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isDisabled}
            aria-label={label}
            onClick={stopPropagation}
          />
        }
      >
        <EyeIcon /> {label}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="dark"
        onClick={stopPropagation}
      >
        <DropdownMenuItem disabled={isDisabled} onClick={() => onExport('pdf')}>
          Preview / Export PDF
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={isDisabled}
          onClick={() => onExport('docx')}
        >
          Preview / Export Word
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={isDisabled}
      onClick={(event) => {
        event.stopPropagation();
        onExport('pdf');
      }}
    >
      <EyeIcon /> {label}
    </Button>
  );

  if (!available) {
    return (
      <span data-tour="ics-form-export">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              render={
                <span className="tw:inline-flex tw:cursor-not-allowed">
                  {button}
                </span>
              }
            />
            <TooltipContent side="top" className="dark tw:max-w-xs">
              {reason}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </span>
    );
  }

  return <span data-tour="ics-form-export">{button}</span>;
}
